import CancelOutlined from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { type CSSProperties, type FC, useCallback } from "react";
import { INK, STATUS, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

export enum DaysInWeek {
	sunday = "Sunday",
	monday = "Monday",
	tuesday = "Tuesday",
	wednesday = "Wednesday",
	thursday = "Thursday",
	friday = "Friday",
	saturday = "Saturday",
}

type DaySelectorSize = "small" | "medium" | "large";
type sizeVariants = { xs?: string | number; md?: string | number; lg?: string | number; xl?: string | number; xxl?: string | number };
const SIZE_CONFIG: Record<DaySelectorSize, { size: sizeVariants; fontSize: sizeVariants }> = {
	small: {
		size: {
			xs: "18px",
			lg: "20px",
		},
		fontSize: {
			xs: "10px",
			lg: "11px",
		},
	},
	medium: {
		size: {
			xs: "22px",
			lg: "28px",
		},
		fontSize: {
			xs: "11px",
			lg: "12px",
		},
	},
	large: {
		size: {
			xs: "26px",
			lg: "34px",
		},
		fontSize: {
			xs: "12px",
			lg: "14px",
		},
	},
};

type Props = {
	selectedDays?: DaysInWeek[];
	onChange?: (selectedDays: DaysInWeek[]) => void;
	width?: CSSProperties["width"];
	gap?: CSSProperties["gap"];
	required?: boolean;
	variant?: "default" | "compact";
	disabledDays?: DaysInWeek[];
	disabledTooltipText?: string;
	size?: DaySelectorSize;
	errorDays?: DaysInWeek[];
	weekStartDay?: number;
};

const ALL_DAYS = Object.values(DaysInWeek);

const getOrderedDays = (weekStartDay: number): DaysInWeek[] => {
	const offset = ((weekStartDay % 7) + 7) % 7;
	return offset === 0 ? ALL_DAYS : [...ALL_DAYS.slice(offset), ...ALL_DAYS.slice(0, offset)];
};

export const DaysInWeekSelector: FC<Props> = ({
	selectedDays,
	width = "fit-content",
	onChange,
	gap = 2,
	required,
	variant = "default",
	disabledDays,
	disabledTooltipText,
	size = "large",
	errorDays,
	weekStartDay = 0,
}) => {
	const orderedDays = getOrderedDays(weekStartDay);
	const theme = useTheme();
	const selectedDayColor = theme.palette.primary.main;
	const unselectedDayColor = alpha(theme.palette.primary.main, 0.1);
	const sizeConfig = SIZE_CONFIG[size];
	const handleSelectToggle = useCallback(
		(day: DaysInWeek, currentlySelected: boolean) => {
			if (onChange) {
				const newSelection = currentlySelected ? selectedDays?.filter((d) => d !== day) : [...(selectedDays || []), day].slice(0, 7);
				onChange(newSelection ?? []);
			}
		},
		[selectedDays, onChange],
	);

	return (
		<Box
			sx={{
				display: "flex",
				width: width,
				gap: gap,
				alignItems: "center",
				justifyContent: "space-evenly",
			}}
		>
			{orderedDays.map((day) => {
				const isDisabled = disabledDays?.includes(day) || false;
				const isSelected = selectedDays?.includes(day) || false;
				const isError = errorDays?.includes(day) || false;
				const selectionAvailable = (selectedDays?.length ?? 0) < 7;

				const element = (
					<Box
						key={day}
						data-testid={`day-selector-${day.toLowerCase()}${isSelected ? "-selected" : ""}`}
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							cursor: !isDisabled && (selectionAvailable || isSelected) ? "pointer" : "not-allowed",
							backgroundColor: isSelected ? selectedDayColor : unselectedDayColor,
							borderRadius: "50%",
							width: sizeConfig.size,
							height: sizeConfig.size,
							fontSize: sizeConfig.fontSize,
							color: isSelected ? SURFACE : INK.SECONDARY,
							userSelect: "none",
							opacity: isDisabled ? 0.4 : 1,
							...(isError ? { outline: `2px solid ${STATUS.ERROR.main}` } : {}),
							...(variant === "compact"
								? {
										height: "fit-content",
										fontSize: "12px",
										lineHeight: "100%",
										backgroundColor: undefined,
										flexDirection: "column",
										color: alpha(INK.SECONDARY, 0.5),
										gap: "2px",
									}
								: {}),
						}}
						onClick={() => {
							if (!isDisabled) {
								handleSelectToggle(day, isSelected);
							}
						}}
					>
						{day[0].toUpperCase()}
						{variant === "compact" &&
							(isSelected ? (
								<CheckCircleOutlined
									fontSize={"small"}
									style={{ color: STATUS.SUCCESS.main }}
									onClick={(event) => {
										event.stopPropagation();
										if (!isDisabled) {
											handleSelectToggle(day, isSelected);
										}
									}}
								/>
							) : (
								<CancelOutlined
									fontSize={"small"}
									style={{ color: alpha(INK.SECONDARY, 0.5) }}
									onClick={(event) => {
										event.stopPropagation();
										if (!isDisabled) {
											handleSelectToggle(day, isSelected);
										}
									}}
								/>
							))}
					</Box>
				);
				return isDisabled ? (
					<InHouseTooltip key={day} title={disabledTooltipText}>
						{element}
					</InHouseTooltip>
				) : (
					element
				);
			})}
			{required && (
				<Box
					sx={{
						display: "flex",
						alignItems: "start",
						color: "red",
						userSelect: "none",
					}}
				>
					*
				</Box>
			)}
		</Box>
	);
};
